import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronDown, User2 } from 'lucide-react';
import { UnifiedCartManager, UNIFIED_CART_UPDATE_EVENT } from '../helpers/UnifiedCartManager';
import { checkLoggedIn } from '../helpers/user';
import logo from '../img/logo.png';
import '../css/navbar.css';
import { getData } from '../helpers/ApiSettings';

const Header = () => {
    const [menuItems, setMenuItems] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const [cartItemCount, setCartItemCount] = useState(0);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [accountLink, setAccountLink] = useState('/connexion');
    const menuRef = useRef(null);

    useEffect(() => {
        const checkAuth = async () => {
            const isAuthenticated = await checkLoggedIn();
            setIsLoggedIn(isAuthenticated);
            setAccountLink(isAuthenticated ? '/mon-compte' : '/connexion');
        };
        checkAuth();
        fetchNavigation();
    }, []);

    useEffect(() => {
        // Setup unified cart listeners
        UnifiedCartManager.setupListeners();

        const updateCartCount = () => {
            setCartItemCount(UnifiedCartManager.getTotalItemCount());
        };
        updateCartCount();
        window.addEventListener(UNIFIED_CART_UPDATE_EVENT, updateCartCount);
        return () => window.removeEventListener(UNIFIED_CART_UPDATE_EVENT, updateCartCount);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
                setActiveSubmenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchNavigation = async () => {
        try{
            const response = await getData('/navigation');
            setMenuItems(response.data.data.menuItems || []);
        }catch(e){
            console.error('Error fetching menu items: ', e);
        }
    };

    const toggleMenu = () => setMenuOpen(!menuOpen);
    const toggleSubmenu = (index, e) => {
        e?.preventDefault();
        setActiveSubmenu(activeSubmenu === index ? null : index);
    };

    return (
        <header className="main-header">
            <nav className="navbar navbar-expand-lg">
                <div className="container">
                    {/* Logo */}
                    <Link className="navbar-brand" to="/">
                        <img src={logo} alt="Logo OVLM" className="logo" />
                    </Link>

                    {/* Mobile Menu Toggle */}
                    <div className="mobile-actions d-lg-none">
                        <Link to="/panier" className="mobile-cart">
                            <ShoppingCart size={24} />
                            {cartItemCount > 0 && (
                                <span className="cart-badge">{cartItemCount}</span>
                            )}
                        </Link>
                        <button className="menu-toggle" onClick={toggleMenu}>
                            <Menu size={24} />
                        </button>
                    </div>

                    {/* Main Navigation */}
                    <div className={`navbar-collapse ${menuOpen ? 'show' : ''}`} ref={menuRef}>
                        <button className="close-menu d-lg-none" onClick={toggleMenu}>
                            <X size={24} />
                        </button>

                        {/* Main Menu */}
                        <ul className="navbar-nav mx-auto">
                            {menuItems.map((item, index) => (
                                <li key={index} className="nav-item">
                                    {item.submenu ? (
                                        <div className="nav-dropdown">
                                            <a
                                                href="#"
                                                className="nav-link"
                                                onClick={(e) => toggleSubmenu(index, e)}
                                            >
                                                <span>{item.name}</span>
                                                <ChevronDown 
                                                    size={16} 
                                                    className={`dropdown-arrow ${activeSubmenu === index ? 'active' : ''}`}
                                                />
                                            </a>
                                            <div className={`dropdown-menu ${activeSubmenu === index ? 'show' : ''}`}>
                                                {item.submenu.map((subItem, subIndex) => (
                                                    <Link 
                                                        key={subIndex}
                                                        to={subItem.link} 
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            toggleMenu();
                                                            setActiveSubmenu(null);
                                                        }}
                                                    >
                                                        {subItem.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <Link 
                                            to={item.link} 
                                            className="nav-link"
                                            onClick={toggleMenu}
                                        >
                                            {item.name}
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Desktop Actions */}
                        <div className="desktop-actions d-none d-lg-flex">
                            <Link to="/panier" className="action-link cart-link">
                                <ShoppingCart size={22} />
                                {cartItemCount > 0 && (
                                    <span className="cart-badge">{cartItemCount}</span>
                                )}
                            </Link>
                            <Link to={accountLink} className="action-link account-link">
                                <User2 size={22} />
                                <span>{isLoggedIn ? 'Mon Compte' : 'Connexion'}</span>
                            </Link>
                        </div>

                        {/* Mobile Account Link */}
                        <div className="mobile-account d-lg-none">
                            <Link to={accountLink} className="action-link" onClick={toggleMenu}>
                                <User2 size={22} />
                                <span>{isLoggedIn ? 'Mon Compte' : 'Connexion'}</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Header;